import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import Popup from "../../components/Popup";
import {
    FORM3_MIN_SUBJECTS,
    fetchForm3SelectionStatus,
    submitForm3SubjectSelection,
} from "../../utils/form3SubjectSelection";
import { useStudentSubjectSelectionStatus } from "../../hooks/useStudentSubjectSelectionStatus";
import { dedupeSubjectsById } from "../../utils/dedupeSubjects";
import { StudentPageContainer, StudentPageHeader } from "./studentPortalUi";

const StudentForm3SubjectChoice = () => {
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const { refetch: refetchPortalStatus } = useStudentSubjectSelectionStatus();
    const studentId = currentUser?._id || currentUser?.id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);
    const [classId, setClassId] = useState("");
    const [selected, setSelected] = useState(() => new Set());
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const load = useCallback(async () => {
        if (!studentId) return;
        setLoading(true);
        try {
            const data = await fetchForm3SelectionStatus(studentId, classId || undefined);
            setStatus(data);
            if (data.classId && !classId) setClassId(data.classId);
            if (Array.isArray(data.chosenSubjectIds) && data.chosenSubjectIds.length) {
                setSelected(new Set(data.chosenSubjectIds));
            }
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not load subjects.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [studentId, classId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!status?.classId || classId) return;
        setClassId(status.classId);
    }, [status?.classId, classId]);

    const subjects = useMemo(
        () => dedupeSubjectsById(Array.isArray(status?.subjects) ? status.subjects : []),
        [status],
    );
    const openClasses = useMemo(() => (Array.isArray(status?.openClasses) ? status.openClasses : []), [status]);
    const selectedCount = selected.size;
    const canSubmit = selectedCount >= FORM3_MIN_SUBJECTS && Boolean(classId) && !saving;

    const toggleSubject = (subjectId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(subjectId)) next.delete(subjectId);
            else next.add(subjectId);
            return next;
        });
    };

    const handleClassChange = async (nextClassId) => {
        setClassId(nextClassId);
        setSelected(new Set());
        if (!studentId || !nextClassId) return;
        setLoading(true);
        try {
            const data = await fetchForm3SelectionStatus(studentId, nextClassId);
            setStatus(data);
        } catch (e) {
            setMessage(e.response?.data?.message || "Could not load class subjects.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!canSubmit || !studentId) return;
        setSaving(true);
        try {
            const result = await submitForm3SubjectSelection(studentId, {
                classId,
                subjectIds: [...selected],
            });
            const refreshed = await refetchPortalStatus();
            if (refreshed) setStatus(refreshed);
            setMessage(result.message || "Subjects saved. Enrollment is pending verification.");
            setShowPopup(true);
            navigate("/Student/dashboard", { replace: true });
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not save your subject choices.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    if (loading && !status) {
        return (
            <StudentPageContainer>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            </StudentPageContainer>
        );
    }

    if (status?.carriedForward && status?.formLevel === "Form4") {
        return (
            <StudentPageContainer maxWidth="md">
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                    You are in Form 4 and continue with your{" "}
                    <strong>{status.chosenSubjectIds?.length || status.subjects?.length || ""}</strong> selected
                    subject(s) from Form 3. No new subject choice is required.
                </Alert>
                <Button sx={{ mt: 2, mr: 1 }} variant="contained" onClick={() => navigate("/Student/subjects")}>
                    View my subjects
                </Button>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate("/Student/dashboard")}>
                    Back to home
                </Button>
            </StudentPageContainer>
        );
    }

    if (status?.complete) {
        const doneLabel = status?.formLevel === "Form4" ? "Form 4" : "Form 3";
        return (
            <StudentPageContainer maxWidth="md">
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                    You have already chosen your {doneLabel} subjects and are enrolled in{" "}
                    <strong>{status.currentClass?.sclassName || "your class"}</strong>.
                </Alert>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={() => navigate("/Student/dashboard", { replace: true })}>
                        Back to home
                    </Button>
                    <Button variant="outlined" onClick={() => navigate("/Student/subjects", { replace: true })}>
                        View my subjects
                    </Button>
                </Stack>
            </StudentPageContainer>
        );
    }

    if (
        status?.reason === "no_ready_class" ||
        status?.reason === "no_open_class" ||
        status?.reason === "form4_no_ready_class"
    ) {
        const waitLabel = status?.formLevel === "Form4" ? "Form 4" : "Form 3";
        return (
            <StudentPageContainer maxWidth="md">
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Your {waitLabel} class does not have enough subjects listed yet. Ask your director to add at least{" "}
                    {FORM3_MIN_SUBJECTS} subjects to your class, then you can complete your subject line here.
                </Alert>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate("/Student/dashboard")}>
                    Back to home
                </Button>
            </StudentPageContainer>
        );
    }

    const formLabel = status?.formLevel === "Form4" ? "Form 4" : "Form 3";

    return (
        <StudentPageContainer maxWidth="md">
            <StudentPageHeader
                eyebrow={`${formLabel} enrollment`}
                title="Choose your subjects"
                subtitle={
                    status?.reason === "choose_subjects_at_enrollment" ||
                    status?.reason === "form4_choose_at_enrollment"
                        ? `You are in ${formLabel} but have not chosen your subjects yet. Pick at least ${FORM3_MIN_SUBJECTS} to complete enrollment.`
                        : `Select at least ${FORM3_MIN_SUBJECTS} subjects. When you submit, you enroll in that ${formLabel} class for director verification.`
                }
            />

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                }}
            >
                {openClasses.length > 1 ? (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{formLabel} class</InputLabel>
                        <Select
                            label={`${formLabel} class`}
                            value={classId}
                            onChange={(e) => handleClassChange(e.target.value)}
                        >
                            {openClasses.map((c) => (
                                <MenuItem key={c.id || c._id} value={c.id || c._id}>
                                    {c.sclassName} ({c.subjectCount} subjects offered)
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : openClasses[0] ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Class: <strong>{openClasses[0].sclassName}</strong>
                    </Typography>
                ) : null}

                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <MenuBookOutlinedIcon color="primary" />
                    <Chip
                        label={`${selectedCount} / ${FORM3_MIN_SUBJECTS} minimum selected`}
                        color={selectedCount >= FORM3_MIN_SUBJECTS ? "success" : "warning"}
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                    />
                </Stack>

                {subjects.length === 0 ? (
                    <Alert severity="warning">No subjects are listed for this class yet.</Alert>
                ) : (
                    <FormGroup>
                        {subjects.map((sub) => {
                            const sid = sub._id || sub.id;
                            return (
                                <Paper
                                    key={sid}
                                    variant="outlined"
                                    sx={{
                                        mb: 1,
                                        px: 1,
                                        borderRadius: 2,
                                        bgcolor: selected.has(sid)
                                            ? (t) => alpha(t.palette.primary.main, 0.06)
                                            : "transparent",
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selected.has(sid)}
                                                onChange={() => toggleSubject(sid)}
                                            />
                                        }
                                        label={
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography fontWeight={600}>{sub.subName}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={sub.isElective ? "Elective" : "Compulsory"}
                                                    variant="outlined"
                                                />
                                            </Stack>
                                        }
                                        sx={{ m: 0, width: "100%", py: 0.5 }}
                                    />
                                </Paper>
                            );
                        })}
                    </FormGroup>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={() => navigate("/Student/dashboard")} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SendOutlinedIcon />}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                        sx={{ fontWeight: 700 }}
                    >
                        {saving ? "Submitting…" : `Enroll with ${selectedCount} subjects`}
                    </Button>
                </Stack>
            </Paper>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </StudentPageContainer>
    );
};

export default StudentForm3SubjectChoice;
